import { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth, homePathFor } from "../context/AuthContext";
import { formatApiErrorDetail } from "../lib/api";
import { Loader2, UserRound, Pill } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { PasswordInput } from "../components/PasswordInput";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { AuthShell } from "./Login";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [role, setRole] = useState(["medico", "isf"].includes(params.get("ruolo")) ? params.get("ruolo") : "");
  const [form, setForm] = useState({
    nome: "", cognome: "", email: params.get("email") || "", password: "",
    telefono: "", azienda: "", specializzazione: "",
  });
  const [privacy, setPrivacy] = useState(false);
  const [cookie, setCookie] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!role) { setError("Seleziona il tuo ruolo."); return; }
    if (!privacy || !cookie) { setError("Devi accettare la Privacy Policy e la Cookie Policy."); return; }
    setLoading(true);
    try {
      const user = await register({
        ...form, role, privacy_consent: privacy, cookie_consent: cookie,
      });
      toast.success("Registrazione completata!");
      navigate(homePathFor(user));
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell>
      <div className="rounded-2xl border border-slate-200 bg-white p-8">
        <h1 className="font-heading text-2xl font-800 text-slate-900">Crea il tuo account</h1>
        <p className="mt-1 text-sm text-slate-500">Compila i campi per registrarti alla piattaforma.</p>

        {/* Role selector */}
        <div className="mt-6">
          <Label>Ruolo</Label>
          <div className="mt-2 grid grid-cols-2 gap-3">
            {[
              { v: "medico", label: "Medico", icon: UserRound },
              { v: "isf", label: "Informatore (ISF)", icon: Pill },
            ].map((r) => (
              <button
                key={r.v}
                type="button"
                onClick={() => setRole(r.v)}
                data-testid={`role-${r.v}-btn`}
                className={`flex items-center gap-2 rounded-lg border p-3 text-sm font-600 transition-colors ${
                  role === r.v ? "border-blue-700 bg-sky-50 text-blue-700" : "border-slate-200 text-slate-600 hover:bg-slate-50"
                }`}
              >
                <r.icon className="h-4 w-4" /> {r.label}
              </button>
            ))}
          </div>
        </div>

        <form onSubmit={submit} className="mt-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="nome">Nome</Label>
              <Input id="nome" value={form.nome} onChange={set("nome")} required className="mt-1.5" data-testid="reg-nome-input" />
            </div>
            <div>
              <Label htmlFor="cognome">Cognome</Label>
              <Input id="cognome" value={form.cognome} onChange={set("cognome")} required className="mt-1.5" data-testid="reg-cognome-input" />
            </div>
          </div>
          <div>
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" value={form.email} onChange={set("email")} required className="mt-1.5" data-testid="reg-email-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="tel">Telefono</Label>
              <Input id="tel" value={form.telefono} onChange={set("telefono")} required className="mt-1.5" data-testid="reg-telefono-input" />
            </div>
            <div>
              <Label htmlFor="pwd">Password</Label>
              <PasswordInput id="pwd" value={form.password} onChange={set("password")} required minLength={6} className="mt-1.5" data-testid="reg-password-input" autoComplete="new-password" />
            </div>
          </div>

          {role === "isf" && (
            <div>
              <Label htmlFor="azienda">Azienda Farmaceutica</Label>
              <Input id="azienda" value={form.azienda} onChange={set("azienda")} required className="mt-1.5" data-testid="reg-azienda-input" />
            </div>
          )}
          {role === "medico" && (
            <div>
              <Label htmlFor="spec">Specializzazione / Tipologia di Medicina</Label>
              <Input id="spec" value={form.specializzazione} onChange={set("specializzazione")} required className="mt-1.5" data-testid="reg-spec-input" />
            </div>
          )}

          <div className="space-y-2 rounded-lg bg-slate-50 p-3">
            <label className="flex items-start gap-2 text-xs text-slate-600">
              <Checkbox checked={privacy} onCheckedChange={setPrivacy} data-testid="reg-privacy-checkbox" className="mt-0.5" />
              <span>Ho letto e accetto la{" "}
                <a href="/privacy" target="_blank" rel="noopener noreferrer" className="font-600 text-blue-700 underline" data-testid="privacy-link">Privacy Policy</a> (GDPR).</span>
            </label>
            <label className="flex items-start gap-2 text-xs text-slate-600">
              <Checkbox checked={cookie} onCheckedChange={setCookie} data-testid="reg-cookie-checkbox" className="mt-0.5" />
              <span>Ho letto e accetto la{" "}
                <a href="/cookie" target="_blank" rel="noopener noreferrer" className="font-600 text-blue-700 underline" data-testid="cookie-link">Cookie Policy</a>.</span>
            </label>
          </div>

          {error && (
            <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-600" data-testid="register-error">{error}</p>
          )}
          <Button type="submit" disabled={loading} className="w-full rounded-full bg-blue-700 hover:bg-blue-800" data-testid="register-submit-btn">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Registrati"}
          </Button>
        </form>
        <p className="mt-5 text-center text-sm text-slate-500">
          Hai già un account?{" "}
          <Link to="/login" className="font-600 text-blue-700 hover:underline" data-testid="goto-login-link">Accedi</Link>
        </p>
      </div>
    </AuthShell>
  );
}
